import { useCallback, useEffect, useRef } from "react";

const sameKeys = (keys, cards, keyOf) =>
  keys.length === cards.length && cards.every((card) => keys.includes(keyOf(card)));

// The displayed order, normally `sorted` itself. `holdOrder()` freezes the
// current order for an in-place edit (a card's date changes where it is): the
// next data keeps it, mapped to fresh cards. Anything else releases it: a new
// sort or filter (`view`, always, at once), a different set of cards, or any
// later change of the data `inputs` (another source answering a read). `holdOrder()` returns `thenRead`: given
// a function starting a read (a promise), it starts it once the edited data is
// shown; that read does not release the hold, and the data shown when it
// settles is what later reads compare to.
const sameInputs = (a, b) => a.every((input, index) => input === b[index]);

export const useStableOrder = (sorted, keyOf, inputs, view = []) => {
  const shownRef = useRef([]);
  const latestViewRef = useRef(view);
  const heldRef = useRef(null);
  const latestInputsRef = useRef(inputs);
  const followUpRef = useRef(null);

  const holdOrder = useCallback(() => {
    const hold = { keys: shownRef.current, view: latestViewRef.current, inputs: null, awaitingFollowUp: false };
    heldRef.current = hold;
    const thenRead = (startRead) => {
      followUpRef.current = { hold, startRead };
    };
    return thenRead;
  }, []);

  // Runs after every render: the follow-up read starts once the hold took the
  // edited data, or at once if the hold is gone.
  useEffect(() => {
    const followUp = followUpRef.current;
    if (!followUp) return;
    const { hold, startRead } = followUp;
    if (heldRef.current === hold && hold.inputs === null) return;
    followUpRef.current = null;
    hold.awaitingFollowUp = true;
    const before = latestInputsRef.current;
    // A read can settle before its data renders: then that next render is
    // the one adopted.
    const settle = () => {
      hold.awaitingFollowUp = false;
      const latest = latestInputsRef.current;
      hold.inputs = sameInputs(latest, before) ? null : latest;
    };
    Promise.resolve(startRead()).then(settle, settle);
  });

  // Read and written during render on purpose: the hold must apply to exactly
  // the render that carries the edited data, and to none after it.
  latestInputsRef.current = inputs;
  latestViewRef.current = view;
  const held = heldRef.current;
  let shown = sorted;
  if (held) {
    const adopting = held.inputs === null || held.awaitingFollowUp;
    const unchanged = adopting || sameInputs(inputs, held.inputs);
    if (unchanged && sameInputs(view, held.view) && sameKeys(held.keys, sorted, keyOf)) {
      if (adopting) held.inputs = inputs;
      const byKey = new Map(sorted.map((card) => [keyOf(card), card]));
      shown = held.keys.map((key) => byKey.get(key));
    } else {
      heldRef.current = null;
    }
  }
  shownRef.current = shown.map(keyOf);
  return { shown, holdOrder };
};
